import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ShipmentChart from '../ShipmentChart/ShipmentChart';
import ToggleButton from '../ToggleButton/ToggleButton';
import StatsTable from '../StatsTable/StatsTable';
import DateRangePickerExtended from '../DateRangePickerExtended/DateRangePickerExtended';
import { getFiscalYearStart } from '../../utils/dateUtils';
import './statsLayout.css';
import MonthRangeSelector from '../MonthRangeSelector/MonthRangeSelector';

export default function StatsLayout({ displayed_report }) {
  const isClients = displayed_report === 'clients';
  const [startDate, setStartDate] = useState(
    isClients ? new Date(2020, 0, 1) : getFiscalYearStart()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [timeFrame, setTimeFrame] = useState('month');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  const prevReportRef = useRef(displayed_report);
  const savedDatesRef = useRef({ startDate: getFiscalYearStart(), endDate: new Date() });

  useEffect(() => {
    const prevReport = prevReportRef.current;
    prevReportRef.current = displayed_report;
    if (prevReport === displayed_report) return;

    if (displayed_report === 'clients') {
      savedDatesRef.current = { startDate, endDate };
      setStartDate(new Date(2020, 0, 1));
      setEndDate(new Date());
    } else if (prevReport === 'clients') {
      setStartDate(savedDatesRef.current.startDate);
      setEndDate(savedDatesRef.current.endDate);
    }
  }, [displayed_report]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeFrameOptions = [
    { value: 'week', label: 'Par semaine' },
    { value: 'month', label: 'Par mois' },
  ];
  const formattedStartDate = startDate ? format(startDate, 'dd LLL yyyy', { locale: fr }) : null;
  const formattedEndDate = endDate ? format(endDate, 'dd LLL yyyy', { locale: fr }) : null;

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const getActiveQuickButton = () => {
    const today = new Date();
    if (isSameDay(startDate, new Date(2020, 0, 1)) && isSameDay(endDate, today)) return 'alltime';
    if (isSameDay(startDate, getFiscalYearStart()) && isSameDay(endDate, today)) return 'exercice';
    for (let year = 2020; year <= today.getFullYear(); year++) {
      if (
        isSameDay(startDate, new Date(year, 0, 1)) &&
        isSameDay(endDate, new Date(year, 11, 31))
      ) {
        return year;
      }
    }
    return null;
  };

  const activeButton = getActiveQuickButton();

  const datePickerZone = () => {
    return (
      <div className="stats-layout-chart-datepicker">
        <div className="stats-layout-chart-datepicker-buttons">
          <button
            className="stats-layout-datepicker-button no-background"
            type="button"
            onClick={() => setShowDateRangePicker(true)}
          >
            {`${formattedStartDate} - ${formattedEndDate}`}
          </button>
          <div className="stats-layout-chart-datepicker-buttonsline">
            <button
              className={`stats-layout-datepicker-alltime-button no-background ${
                activeButton === 'alltime' ? 'active' : ''
              }`}
              type="button"
              onClick={() => {
                setStartDate(new Date(2020, 0, 1));
                setEndDate(new Date());
              }}
            >
              Alltime
            </button>
            <button
              className={`stats-layout-datepicker-alltime-button no-background ${
                activeButton === 'exercice' ? 'active' : ''
              }`}
              type="button"
              onClick={() => {
                setStartDate(getFiscalYearStart());
                setEndDate(new Date());
              }}
            >
              Exercice
            </button>
            {getYearButtons()}
          </div>
        </div>
        <MonthRangeSelector
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </div>
    );
  };

  const getYearButtons = () => {
    let yearButtons = [];
    for (let year = 2020; year <= new Date().getFullYear(); year++) {
      yearButtons.push(
        <button
          key={year}
          className={`stats-layout-datepicker-alltime-button no-background ${
            activeButton === year ? 'active' : ''
          }`}
          type="button"
          onClick={() => {
            setStartDate(new Date(year, 0, 1));
            setEndDate(new Date(year, 11, 31));
          }}
        >
          {year}
        </button>
      );
    }
    return yearButtons;
  };

  return (
    <div className="stats-layout-container">
      <div className="date-picker-zone-wrapper">{datePickerZone()}</div>
      <div className="stats-layout-grid">
        <div className="stats-layout-data-table-column">
          <div className="stats-layout-table-container">
            {displayed_report !== 'clients' && displayed_report !== 'litiges' && (
              <div className="stats-layout-table-toggle-button">
                <ToggleButton
                  options={timeFrameOptions}
                  selectedOption={timeFrame}
                  onToggle={option => setTimeFrame(option)}
                />
              </div>
            )}
            <div className="stats-layout-table">
              <StatsTable
                data_type={displayed_report}
                startDate={startDate}
                endDate={endDate}
                timeFrame={timeFrame}
              />
            </div>
          </div>
        </div>
        <div className="stats-layout-chart-column">
          {displayed_report !== 'clients' && (
            <div className="stats-layout-chart-container">
              <div className="stats-layout-chart">
                {displayed_report === 'sales_margin' && (
                  <>
                    <p>Par semaine</p>
                    <ShipmentChart
                      timeFrame="week"
                      startDate={startDate}
                      endDate={endDate}
                      BlueBarDataKey="sales"
                      GreenBarDataKey="margin"
                      lineDataKey="margin_rate"
                      height={300}
                    />
                    <p>Par mois</p>
                    <ShipmentChart
                      timeFrame="month"
                      startDate={startDate}
                      endDate={endDate}
                      BlueBarDataKey="sales"
                      GreenBarDataKey="margin"
                      lineDataKey="margin_rate"
                      height={300}
                    />
                  </>
                )}
                {displayed_report === 'number_of_shipments' && (
                  <ShipmentChart
                    timeFrame="month"
                    startDate={startDate}
                    endDate={endDate}
                    GreenBarDataKey="number_of_shipments"
                    lineDataKey="sales"
                    height={500}
                  />
                )}
              </div>
            </div>
          )}
          {displayed_report === 'clients' && (
            <div className="stats-layout-table-container">
              <p className="stats-layout-table-title">Par groupe client</p>
              <div className="stats-layout-table">
                <StatsTable
                  data_type={displayed_report}
                  startDate={startDate}
                  endDate={endDate}
                  timeFrame={timeFrame}
                  other={'grouped-clients'}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {showDateRangePicker && (
        <DateRangePickerExtended
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          onClose={() => setShowDateRangePicker(false)}
        />
      )}
    </div>
  );
}
